<template>
  <div>
    <div class="d-flex flex-column">
      <div class="invitee-settings-header d-flex align-center mb-2">
        <div
          class="text-grey-darken-4 font-weight-bold invitee-settings-header__title"
        >
          {{ i18n.t('titles.participants_step_participants') }}
        </div>
        <div class="invitee-settings-header__switch d-flex align-center">
          <OrderInviteesSwitch />
        </div>
      </div>
      <v-expand-transition
        v-if="!createStore.hasApprovers && createStore.ordered && userIsSigning"
      >
        <div class="d-flex flex-row flex-grow-1 panel-content mb-2">
          <div class="d-flex flex-row align-center">
            <div class="text-body-medium text-grey-darken-1">
              {{ i18n.t('texts.i_am_signing') }}
            </div>
            <VBtnTertiary
              class="h-auto text-body-medium"
              variant="plain"
              @click="reorderUserGroupLocally(SignOrder.FIRST)"
            >
              {{ i18n.t('actions.first') }}
            </VBtnTertiary>
            <v-icon color="grey-lighten-2" size="16"> fal fa-pipe </v-icon>
            <VBtnTertiary
              class="h-auto text-body-medium"
              variant="plain"
              @click="reorderUserGroupLocally(SignOrder.LAST)"
            >
              {{ i18n.t('actions.last') }}
            </VBtnTertiary>
          </div>
        </div>
      </v-expand-transition>
    </div>
    <slot />
  </div>
</template>
<script setup lang="ts">
import OrderInviteesSwitch from '@/components/create/settings/OrderInviteesSwitch.vue';
import AuthService from '@/services/authService.ts';
import { useI18n } from 'vue-i18n';
import { computed, onBeforeMount } from 'vue';
import type { Signee } from '@/types/Signee.ts';
import SignatureService from '@/services/signatureService.ts';
import { useCreateStore } from '@/stores/create.ts';
import { SignOrder } from '@/types/enums/SignOrder.ts';

const i18n = useI18n();
const createStore = useCreateStore();

const userIsSigning = computed(
  () =>
    !!createStore.signeesOrdered.find((group) =>
      group.find((invitee: Signee) =>
        SignatureService.compareString(invitee?.email, AuthService.user?.email)
      )
    )
);

// The "First"/"Last" buttons only render inside the signing-workflow dialog,
// which always runs deferred (createStore.deferPersistence === true). So we
// reorder the user's group in the local draft; reconcileParticipants() persists
// the final grouping on Confirm — there is no per-click API call.
function reorderUserGroupLocally(type: SignOrder) {
  const doc = createStore.document;
  if (!doc) return;

  const groups = createStore.signeesOrdered.map((group) => [...group]);
  const userGroupIndex = groups.findIndex((group) =>
    group.some((invitee: Signee) =>
      SignatureService.compareString(
        invitee?.email ?? '',
        AuthService.user?.email ?? ''
      )
    )
  );
  if (userGroupIndex === -1) return;

  const [userGroup] = groups.splice(userGroupIndex, 1);
  if (type === SignOrder.FIRST) {
    groups.unshift(userGroup);
  } else {
    groups.push(userGroup);
  }

  groups.forEach((group, index) => {
    group.forEach((invitee: Signee) => {
      invitee.signOrder = index;
    });
  });
  createStore.signeesOrdered = groups;
}

onBeforeMount(async () => {
  createStore.ordered = createStore.signeesOrderedFlat.some(
    (invitee) => invitee.signOrder === 1
  );
});
</script>
<style lang="scss" scoped>
// Title keeps the left edge; the switch group hugs its own content and sits
// flush against the header's right edge, aligned with the invitee rows below.
.invitee-settings-header__title {
  flex: 1 1 auto;
  min-width: 0;
}

.invitee-settings-header__switch {
  flex: 0 1 auto;
  margin-inline-start: auto;
}
</style>
